import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Tree, Waves, BatteryFull, Medal, ArrowRight, ArrowUpRight, CheckCircle, Sparkle,
    Leaf, Globe, Handshake, Trophy, Rocket,
} from "@phosphor-icons/react";
import PageShell from "@/components/dashboard/PageShell";
import PartnerInquiryModal from "@/components/partners/PartnerInquiryModal";
import { Toaster } from "sonner";
import { api, endpoints } from "@/lib/api";
import { PAGES } from "@/constants/testIds";

const PILLARS = [
    {
        icon: Globe,
        color: "#a3e635",
        label: "Environmental Restoration",
        body: "Supporting verified projects that restore forests, improve habitats, and protect natural ecosystems.",
    },
    {
        icon: Waves,
        color: "#06b6d4",
        label: "Ocean & Waterway Protection",
        body: "Helping remove plastic and debris from oceans, rivers, and waterways while promoting healthier environments.",
    },
    {
        icon: BatteryFull,
        color: "#8b5cf6",
        label: "Battery Sustainability",
        body: "Supporting initiatives that promote battery longevity, responsible recycling, resource conservation, and EV education.",
    },
    {
        icon: Medal,
        color: "#f59e0b",
        label: "Veteran Workforce Development",
        body: "Helping veterans transition into careers within the EV, battery technology, renewable energy, and clean transportation industries through education, certification assistance, mentorship, scholarships, and workforce development opportunities.",
    },
];

const TIERS = [
    {
        id: "sustainability",
        icon: Leaf,
        color: "#a3e635",
        name: "Sustainability Partner",
        price: "$2,500",
        priceNote: "Annual investment",
        subtitle: "Designed for local businesses and emerging brands beginning their ESG journey.",
        benefits: [
            "Official EVIQ ESG Partner designation",
            "Company listing on the EVIQ website",
            "Recognition within the EVIQ mobile app",
            "Digital ESG Partner badge",
            "Social media announcement",
            "Annual Community Impact Report",
        ],
    },
    {
        id: "innovation",
        icon: Sparkle,
        color: "#06b6d4",
        name: "Innovation Partner",
        price: "$5,000",
        priceNote: "Annual investment",
        subtitle: "Ideal for organizations expanding their sustainability and community engagement efforts.",
        includesFrom: "Sustainability Partner",
        benefits: [
            "Featured partner profile",
            "Quarterly social media recognition",
            "Priority listing in the Partner Directory",
            "Permission to display the “Proud EVIQ ESG Partner” designation",
            "Quarterly ESG impact updates",
        ],
    },
    {
        id: "leader",
        icon: Tree,
        color: "#a3e635",
        name: "Sustainability Leader",
        price: "$10,000",
        priceNote: "Annual investment",
        subtitle: "Designed for regional and national organizations seeking greater visibility and measurable environmental impact.",
        includesFrom: "Innovation Partner",
        featured: true,
        benefits: [
            "Featured homepage recognition",
            "Sponsorship of an EVIQ Community Impact Campaign",
            "Logo placement within the Annual Impact Report",
            "Co-branded sustainability campaigns",
            "Featured partner article",
            "Trade show recognition",
        ],
    },
    {
        id: "guardian",
        icon: Trophy,
        color: "#f59e0b",
        name: "Earth Guardian Partner",
        price: "$25,000",
        priceNote: "Annual investment",
        subtitle: "For organizations leading the transition toward a cleaner, more sustainable future.",
        includesFrom: "Sustainability Leader",
        benefits: [
            "Premier ESG Partner designation",
            "Featured branding throughout the EVIQ platform",
            "Dedicated impact campaign",
            "Collaborative marketing initiatives",
            "Speaking opportunities at EVIQ events",
            "Executive sustainability recognition",
            "Featured placement within the mobile application",
        ],
    },
    {
        id: "legacy",
        icon: Rocket,
        color: "#c9a55a",
        name: "Legacy Impact Partner",
        price: "$50,000+",
        priceNote: "Annual investment",
        subtitle: "Reserved for industry leaders committed to shaping the future of sustainable transportation.",
        includesFrom: "Earth Guardian",
        premium: true,
        benefits: [
            "Exclusive Legacy Partner designation",
            "Executive advisory relationship with EVIQ",
            "Naming opportunities for select initiatives",
            "National co-branded impact campaigns",
            "Executive media and press opportunities",
            "Customized ESG activation strategy",
            "Annual Executive Impact Briefing",
        ],
    },
];

const REPORTING = [
    "Environmental restoration projects supported",
    "Ocean and waterway cleanup initiatives",
    "Battery sustainability and recycling education programs",
    "Veterans supported through workforce development",
    "Community education initiatives",
    "Estimated critical minerals conserved through battery longevity",
    "Estimated battery replacements deferred through responsible ownership",
    "Estimated greenhouse gas emissions avoided using EVIQ's transparent methodology",
];

const VISIBILITY = [
    "EVIQ Mobile App", "EVIQ Website", "Partner Directory", "Social Media Campaigns",
    "Press Releases", "Annual ESG Impact Report", "Educational Content",
    "Community Events", "Industry Conferences", "Product Launches", "Digital Marketing Campaigns",
];

const IMPACT_METRICS = [
    { label: "Demonstrate measurable ESG and CSR leadership." },
    { label: "Increase visibility among a highly engaged EV audience." },
    { label: "Support verified environmental initiatives." },
    { label: "Invest in America's veterans and the future clean-energy workforce." },
    { label: "Promote responsible battery sustainability and resource conservation." },
    { label: "Participate in measurable annual impact reporting." },
    { label: "Strengthen customer trust through purpose-driven initiatives." },
];

const CONTACT_EMAIL = "support@eviq.pro";

const IMPACT_BADGE_URL =
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_battery-legacy/artifacts/5bs7mro4_EVIQImpactPartner.png";

const FEATURED_PARTNER = {
    name: "CoaterZ",
    website: "https://www.coaterz.com",
    logo: "https://customer-assets-gfyr7b9c.emergentagent.net/job_battery-legacy/artifacts/je9svzyo_CoaterZ.jpg",
    quote: "CoaterZ is a proud EVIQ Legacy Impact Partner — turning ceramic-coating craftsmanship into a measurable engine for forest restoration, veteran careers, and battery sustainability.",
    since: "Feb 2026",
    tier: "Legacy Impact Partner",
};

const Partners = () => {
    const [sponsors, setSponsors] = useState([]);
    const [openTier, setOpenTier] = useState(null);

    useEffect(() => {
        let ignore = false;
        api.get(endpoints.sponsors())
            .then((r) => !ignore && setSponsors(r.data || []))
            .catch(() => {});
        return () => {
            ignore = true;
        };
    }, []);

    const sponsorsByTier = (tierId) =>
        sponsors.filter((s) => (s.tier || "sustainability") === tierId);

    return (
        <PageShell testId={PAGES.partners}>
            <Toaster
                theme="dark"
                position="bottom-right"
                toastOptions={{
                    style: { background: "#0a0a0a", border: "1px solid #27272a", color: "#fff" },
                }}
            />
            <PartnerInquiryModal
                tier={openTier}
                open={Boolean(openTier)}
                onClose={() => setOpenTier(null)}
            />
            <div className="flex flex-col gap-8 sm:gap-12">
                {/* Hero */}
                <section className="relative overflow-hidden rounded-lg border border-[#1f1f22] bg-gradient-to-br from-[#0b0f05] via-[#080808] to-[#050b0d] p-6 sm:p-10 lg:p-14">
                    <div className="absolute -top-24 right-0 h-72 w-72 rounded-full bg-[#a3e635]/10 blur-3xl" />
                    <div className="absolute -bottom-24 left-1/3 h-64 w-64 rounded-full bg-[#06b6d4]/10 blur-3xl" />
                    <div className="relative">
                        <span className="eviq-chip">EVIQ · ESG & Brand Partnership Program</span>
                        <h1 className="mt-6 font-editorial text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl xl:text-7xl">
                            Partner With Purpose.
                            <br />
                            <span className="italic text-zinc-300">Drive Sustainable Innovation.</span>
                            <br />
                            <span className="text-[#a3e635]">Create Lasting Impact.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl font-editorial text-lg leading-relaxed text-zinc-300 sm:text-xl">
                            The EVIQ ESG & Brand Partnership Program connects forward-thinking
                            organizations with one of the fastest-growing communities of electric
                            vehicle owners while advancing meaningful environmental and social
                            initiatives.
                        </p>
                        <p className="mt-5 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
                            Through a strategic partnership with EVIQ, your organization
                            demonstrates leadership in sustainability, strengthens its
                            Environmental, Social & Governance (ESG) commitments, and aligns its
                            brand with a community dedicated to innovation, responsible resource
                            management, and clean transportation.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <a
                                href={`mailto:${CONTACT_EMAIL}?subject=EVIQ%20ESG%20%26%20Brand%20Partnership%20Inquiry`}
                                data-testid="partners-cta-primary"
                                className="inline-flex items-center gap-2 rounded-full bg-[#a3e635] px-6 py-3 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5"
                            >
                                Become an EVIQ ESG Partner
                                <ArrowRight size={14} weight="bold" />
                            </a>
                            <a
                                href="#tiers"
                                className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-[#0a0a0a] px-6 py-3 text-sm text-zinc-200 hover:border-[#a3e635]/40"
                            >
                                View partnership levels
                            </a>
                        </div>
                    </div>
                </section>

                {/* Featured launch partner */}
                <section
                    data-testid="partners-featured-coaterz"
                    className="relative overflow-hidden rounded-lg border border-[#c9a55a]/40 bg-gradient-to-br from-[#0d0a05] via-[#080606] to-[#050505] p-6 sm:p-10 lg:p-12"
                >
                    <div className="absolute -top-24 right-1/4 h-64 w-64 rounded-full bg-[#c9a55a]/15 blur-3xl" />
                    <div className="absolute -bottom-24 left-0 h-56 w-56 rounded-full bg-[#a3e635]/10 blur-3xl" />
                    <div className="relative grid grid-cols-1 items-center gap-8 lg:grid-cols-[1.2fr_1fr]">
                        <div>
                            <span className="inline-flex items-center gap-2 rounded-full border border-[#c9a55a]/40 bg-black/60 px-3 py-1 font-mono-metric text-[10px] uppercase tracking-[0.28em] text-[#c9a55a]">
                                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#c9a55a]" />
                                Featured Partner · {FEATURED_PARTNER.since}
                            </span>
                            <h2 className="mt-5 font-editorial text-3xl leading-tight text-white sm:text-4xl md:text-5xl">
                                <a
                                    href={FEATURED_PARTNER.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="transition-colors hover:text-[#c9a55a]"
                                >
                                    {FEATURED_PARTNER.name}
                                </a>
                            </h2>
                            <p className="mt-2 font-mono-metric text-[11px] uppercase tracking-[0.24em] text-[#c9a55a]">
                                {FEATURED_PARTNER.tier}
                            </p>
                            <p className="mt-5 max-w-xl font-editorial text-lg italic leading-relaxed text-zinc-300">
                                “{FEATURED_PARTNER.quote}”
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <a
                                    href={FEATURED_PARTNER.website}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="partners-featured-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-[#c9a55a] px-5 py-2.5 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5"
                                >
                                    Visit CoaterZ
                                    <ArrowUpRight size={13} weight="bold" />
                                </a>
                                <Link
                                    to="/partners/coaterz"
                                    className="inline-flex items-center gap-2 rounded-full border border-[#27272a] bg-black/50 px-5 py-2.5 text-sm text-zinc-200 hover:border-[#c9a55a]/40"
                                >
                                    Attribution report
                                </Link>
                            </div>
                            <div className="mt-6 flex items-center gap-3 border-t border-[#1f1f22] pt-5">
                                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-[#27272a] bg-black">
                                    <img
                                        src={FEATURED_PARTNER.logo}
                                        alt="CoaterZ logo"
                                        className="h-full w-full object-cover"
                                        loading="lazy"
                                    />
                                </div>
                                <div className="min-w-0">
                                    <p className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                        Ceramic coating · Automotive protection
                                    </p>
                                    <p className="truncate text-sm text-zinc-200">
                                        www.coaterz.com
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="mx-auto flex w-full max-w-[420px] justify-center lg:mx-0 lg:ml-auto">
                            <div className="relative aspect-square w-full max-w-[380px]">
                                <div className="absolute inset-0 rounded-full bg-[#c9a55a]/20 blur-2xl" />
                                <img
                                    src={IMPACT_BADGE_URL}
                                    alt="EVIQ Verified Impact Partner"
                                    className="relative h-full w-full rounded-full object-cover drop-shadow-[0_20px_60px_rgba(201,165,90,0.35)]"
                                    loading="eager"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Why partner */}
                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-10">
                    <span className="eviq-chip">Why partner with EVIQ?</span>
                    <h2 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl">
                        Purpose that shows up on your ESG scorecard.
                    </h2>
                    <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {IMPACT_METRICS.map((m) => (
                            <div
                                key={m.label}
                                className="flex items-start gap-3 rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-4"
                            >
                                <CheckCircle size={16} weight="fill" color="#a3e635" className="mt-0.5 flex-shrink-0" />
                                <span className="text-sm text-zinc-300">{m.label}</span>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Four pillars */}
                <section>
                    <span className="eviq-chip">Our Four Pillars of Impact</span>
                    <h2 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl">
                        Where every partnership dollar quietly goes to work.
                    </h2>
                    <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {PILLARS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <div
                                    key={p.label}
                                    data-testid={`partners-pillar-${p.label.toLowerCase().split(" ")[0]}`}
                                    className="group rounded-lg border border-[#1f1f22] bg-[#080808] p-6 transition-colors hover:border-[#a3e635]/30 sm:p-8"
                                >
                                    <div
                                        className="flex h-12 w-12 items-center justify-center rounded-full"
                                        style={{ background: `${p.color}18`, border: `1px solid ${p.color}55` }}
                                    >
                                        <Icon size={22} weight="fill" color={p.color} />
                                    </div>
                                    <h3 className="mt-5 font-editorial text-xl text-white sm:text-2xl">
                                        {p.label}
                                    </h3>
                                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">
                                        {p.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Tiers */}
                <section id="tiers">
                    <div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <span className="eviq-chip">ESG Partnership Levels</span>
                            <h2 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl">
                                Five ways to lead.
                            </h2>
                        </div>
                        <p className="max-w-sm text-xs text-zinc-500">
                            Every tier includes an Annual Community Impact Report and everything
                            in the tier below.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {TIERS.map((tier) => {
                            const Icon = tier.icon;
                            const isPremium = Boolean(tier.premium);
                            const isFeatured = Boolean(tier.featured);
                            return (
                                <div
                                    key={tier.id}
                                    data-testid={`partners-tier-${tier.id}`}
                                    className={`relative flex flex-col rounded-lg border p-6 transition-colors sm:p-7 ${
                                        isPremium
                                            ? "border-[#c9a55a]/50 bg-gradient-to-br from-[#0d0a05] via-[#080605] to-[#050505]"
                                            : isFeatured
                                            ? "border-[#a3e635]/40 bg-gradient-to-br from-[#0a0f05] via-[#080808] to-[#050505]"
                                            : "border-[#1f1f22] bg-[#080808] hover:border-[#a3e635]/25"
                                    }`}
                                >
                                    {isFeatured && (
                                        <span className="absolute -top-3 left-6 rounded-full border border-[#a3e635] bg-black px-3 py-0.5 font-mono-metric text-[10px] uppercase tracking-[0.22em] text-[#a3e635]">
                                            Most popular
                                        </span>
                                    )}
                                    {isPremium && (
                                        <span className="absolute -top-3 left-6 rounded-full border border-[#c9a55a] bg-black px-3 py-0.5 font-mono-metric text-[10px] uppercase tracking-[0.22em] text-[#c9a55a]">
                                            Premier
                                        </span>
                                    )}
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="flex h-10 w-10 items-center justify-center rounded-full"
                                            style={{
                                                background: `${tier.color}18`,
                                                border: `1px solid ${tier.color}55`,
                                            }}
                                        >
                                            <Icon size={18} weight="fill" color={tier.color} />
                                        </div>
                                        <h3 className="font-editorial text-lg text-white sm:text-xl">
                                            {tier.name}
                                        </h3>
                                    </div>
                                    <div className="mt-5">
                                        <span
                                            className="font-mono-metric text-4xl font-bold sm:text-5xl"
                                            style={{ color: tier.color }}
                                        >
                                            {tier.price}
                                        </span>
                                        <div className="mt-1 font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                            {tier.priceNote}
                                        </div>
                                    </div>
                                    <p className="mt-4 text-sm text-zinc-400">{tier.subtitle}</p>
                                    {tier.includesFrom && (
                                        <p className="mt-4 rounded-md border border-[#1f1f22] bg-black/40 px-3 py-2 font-mono-metric text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                            Includes everything in {tier.includesFrom}
                                        </p>
                                    )}
                                    <ul className="mt-4 flex flex-col gap-2 text-sm text-zinc-300">
                                        {tier.benefits.map((b) => (
                                            <li key={b} className="flex items-start gap-2">
                                                <CheckCircle
                                                    size={14}
                                                    weight="fill"
                                                    color={tier.color}
                                                    className="mt-0.5 flex-shrink-0"
                                                />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        type="button"
                                        data-testid={`partners-tier-cta-${tier.id}`}
                                        onClick={() => setOpenTier(tier)}
                                        className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-transform duration-200 hover:-translate-y-0.5 ${
                                            isPremium || isFeatured
                                                ? "bg-[#a3e635] text-black"
                                                : "border border-[#27272a] bg-black text-zinc-100 hover:border-[#a3e635]/40"
                                        }`}
                                    >
                                        Inquire about {tier.name.split(" ")[0]}
                                        <ArrowRight size={12} weight="bold" />
                                    </button>

                                    {/* Live partner roster for this tier */}
                                    {(() => {
                                        const list = sponsorsByTier(tier.id);
                                        if (!list.length) return null;
                                        return (
                                            <div
                                                data-testid={`partners-directory-${tier.id}`}
                                                className="mt-6 border-t border-[#1f1f22] pt-4"
                                            >
                                                <div className="mb-3 flex items-center justify-between">
                                                    <span className="font-mono-metric text-[10px] uppercase tracking-[0.22em] text-zinc-500">
                                                        Current partners
                                                    </span>
                                                    <span className="font-mono-metric text-[10px] text-zinc-600">
                                                        {list.length}
                                                    </span>
                                                </div>
                                                <div className="flex flex-col gap-2">
                                                    {list.map((sp) => {
                                                        const slug = sp.name
                                                            .toLowerCase()
                                                            .replace(/[^a-z0-9]+/g, "-")
                                                            .replace(/^-|-$/g, "");
                                                        const inner = (
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded border border-[#27272a] bg-black">
                                                                    {sp.logo_url && !sp.logo_url.startsWith("data:image/svg+xml") ? (
                                                                        <img
                                                                            src={sp.logo_url}
                                                                            alt={sp.name}
                                                                            className="h-full w-full object-contain"
                                                                            loading="lazy"
                                                                        />
                                                                    ) : (
                                                                        <span className="font-mono-metric text-xs font-bold" style={{ color: tier.color }}>
                                                                            {sp.name.charAt(0)}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className="truncate text-xs text-zinc-200">
                                                                    {sp.name}
                                                                </span>
                                                                {sp.website_url && (
                                                                    <ArrowUpRight
                                                                        size={11}
                                                                        className="ml-auto flex-shrink-0 text-zinc-500"
                                                                    />
                                                                )}
                                                            </div>
                                                        );
                                                        return sp.website_url ? (
                                                            <a
                                                                key={sp.name}
                                                                href={sp.website_url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                data-testid={`partners-directory-item-${slug}`}
                                                                className="rounded-md border border-[#1f1f22] bg-black/40 px-3 py-2 hover:border-[#a3e635]/30"
                                                            >
                                                                {inner}
                                                            </a>
                                                        ) : (
                                                            <div
                                                                key={sp.name}
                                                                data-testid={`partners-directory-item-${slug}`}
                                                                className="rounded-md border border-[#1f1f22] bg-black/40 px-3 py-2"
                                                            >
                                                                {inner}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            );
                        })}
                    </div>
                </section>

                {/* Impact reporting */}
                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-10">
                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_1.2fr]">
                        <div>
                            <span className="eviq-chip">ESG Impact Reporting</span>
                            <h2 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl">
                                Data that stands up to your board.
                            </h2>
                            <p className="mt-4 text-sm leading-relaxed text-zinc-400 sm:text-base">
                                Each ESG Partner receives an Annual Impact Report summarizing the
                                measurable outcomes made possible through their partnership.
                                Metrics feed directly into your ESG, Sustainability, CSR,
                                Investor, and Marketing communications.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-2">
                                {[
                                    "ESG Reports",
                                    "Sustainability Reports",
                                    "CSR Reports",
                                    "Annual Reports",
                                    "Investor Communications",
                                    "Marketing Campaigns",
                                    "Brand Storytelling",
                                ].map((chip) => (
                                    <span
                                        key={chip}
                                        className="rounded-full border border-[#1f1f22] bg-[#0a0a0a] px-3 py-1 text-xs text-zinc-400"
                                    >
                                        {chip}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            {REPORTING.map((r) => (
                                <li
                                    key={r}
                                    className="flex items-start gap-2 rounded-md border border-[#1f1f22] bg-[#0a0a0a] p-3 text-xs leading-relaxed text-zinc-300 sm:text-sm"
                                >
                                    <CheckCircle
                                        size={14}
                                        weight="fill"
                                        color="#a3e635"
                                        className="mt-0.5 flex-shrink-0"
                                    />
                                    {r}
                                </li>
                            ))}
                        </ul>
                    </div>
                </section>

                {/* Brand visibility */}
                <section className="rounded-lg border border-[#1f1f22] bg-[#080808] p-6 sm:p-10">
                    <span className="eviq-chip">Brand Visibility</span>
                    <h2 className="mt-4 font-editorial text-3xl leading-tight text-white sm:text-4xl">
                        Where your logo shows up.
                    </h2>
                    <div className="mt-6 flex flex-wrap gap-2">
                        {VISIBILITY.map((v) => (
                            <span
                                key={v}
                                className="rounded-full border border-[#a3e635]/25 bg-[#0a0f05] px-3 py-1.5 text-xs text-[#a3e635]"
                            >
                                {v}
                            </span>
                        ))}
                    </div>
                </section>

                {/* Closing */}
                <section className="rounded-lg border border-[#1f1f22] bg-gradient-to-br from-[#0b0f05] via-[#080808] to-[#050b0d] p-6 text-center sm:p-14">
                    <span className="eviq-chip">Together We Create Impact</span>
                    <h2 className="mx-auto mt-4 max-w-3xl font-editorial text-3xl leading-tight text-white sm:text-4xl md:text-5xl">
                        The future of mobility isn't about vehicles.
                        <br />
                        <span className="italic text-zinc-400">It's about measurable, lasting impact.</span>
                    </h2>
                    <p className="mx-auto mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
                        Join a growing network of organizations committed to innovation,
                        sustainability, and meaningful change. Partner with EVIQ and help shape
                        the future of clean transportation — one battery, one community, and one
                        generation at a time.
                    </p>
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                        <a
                            href={`mailto:${CONTACT_EMAIL}?subject=EVIQ%20ESG%20%26%20Brand%20Partnership%20Inquiry`}
                            data-testid="partners-cta-secondary"
                            className="inline-flex items-center gap-2 rounded-full bg-[#a3e635] px-6 py-3 text-sm font-semibold text-black transition-transform duration-200 hover:-translate-y-0.5"
                        >
                            <Handshake size={14} weight="bold" />
                            Become an EVIQ ESG Partner
                        </a>
                        <Link
                            to="/dashboard"
                            className="text-sm text-zinc-400 hover:text-zinc-100"
                        >
                            Back to Dashboard →
                        </Link>
                    </div>
                    <p className="mt-6 font-mono-metric text-[10px] uppercase tracking-[0.28em] text-zinc-600">
                        Inquiries · {CONTACT_EMAIL}
                    </p>
                </section>
            </div>
        </PageShell>
    );
};

export default Partners;
